import { useCallback, useEffect, useRef } from "react";

// Computes one smoothed 0–1 audio level and reports it via onLevel every frame:
// the mic stream while listening, the TTS output analyser while the assistant
// speaks, and a gentle sine pulse while thinking. Same analyser/peak-decay
// technique as useLiveMicVisualizer, minus the waveform canvas — the consumer
// (VoiceOrb) turns the level into a re-render-scoped volume prop.

// Synthetic pulse for `thinking`, which has no audio to measure. Range
// 0.14–0.42, comparable to a mid-volume voice. The peak decay below settles
// far faster at 60fps than this period, so the sine survives the smoothing.
const THINKING_BASE = 0.28;
const THINKING_SWING = 0.14;
const THINKING_PERIOD_MS = 360;

const useVoiceOrbLevel = ({ micStream, ttsAnalyser, mode, onLevel }) => {
  const frameIdRef = useRef(null);
  const micTapRef = useRef(null);
  // Keep the latest callback without re-running the analyser effect.
  const onLevelRef = useRef(onLevel);
  onLevelRef.current = onLevel;

  const stopMicTap = useCallback(() => {
    micTapRef.current?.source?.disconnect?.();
    micTapRef.current?.audioContext?.close?.().catch(() => {});
    micTapRef.current = null;
  }, []);

  useEffect(() => {
    let analyser = null;

    if (mode === "speaking" && ttsAnalyser) {
      analyser = ttsAnalyser;
    } else if (mode === "listening" && micStream) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const audioContext = new AudioContextClass();
        const micAnalyser = audioContext.createAnalyser();
        micAnalyser.fftSize = 128;
        micAnalyser.smoothingTimeConstant = 0.82;
        const source = audioContext.createMediaStreamSource(micStream);
        source.connect(micAnalyser);
        micTapRef.current = { audioContext, source };
        analyser = micAnalyser;
      }
    }

    let peakLevel = 0;
    const dataArray = analyser
      ? new Uint8Array(analyser.frequencyBinCount)
      : null;

    const drawFrame = () => {
      let level = 0;
      if (analyser && dataArray) {
        analyser.getByteFrequencyData(dataArray);
        let total = 0;
        for (let i = 0; i < dataArray.length; i += 1) total += dataArray[i];
        level = total / dataArray.length / 255;
      } else if (mode === "thinking") {
        // No audio while the agent works, so the pulse is synthetic. Thinking
        // is now the loud state (orange orb, user waiting on it), so it sits in
        // the same band a mid-volume voice would produce rather than idling.
        level =
          THINKING_BASE + Math.sin(Date.now() / THINKING_PERIOD_MS) * THINKING_SWING;
      }

      peakLevel = Math.max(level, peakLevel * 0.9);
      onLevelRef.current?.(peakLevel);
      frameIdRef.current = window.requestAnimationFrame(drawFrame);
    };
    drawFrame();

    return () => {
      window.cancelAnimationFrame(frameIdRef.current);
      stopMicTap();
      // Settle the orb back to calm when the source goes away.
      onLevelRef.current?.(0);
    };
  }, [mode, micStream, ttsAnalyser, stopMicTap]);
};

export default useVoiceOrbLevel;
